import type { ComponentProps, ComponentPropsWithoutRef, ReactNode } from 'react'

import { Link } from '@tanstack/react-router'
import ReactIs from 'react-is'

import { Button } from '@/components/ui/button'
import { Container } from '@/components/ui/container'
import { Heading } from '@/components/ui/heading'
import { Text } from '@/components/ui/text'

import { cx } from '@/libs/cva'

function Root({
	className,
	...rest
}: ComponentPropsWithoutRef<typeof Container>) {
	return <Container className={cx('divide-y p-0', className)} {...rest} />
}

interface HeaderProps {
	title: string
	desc?: string
	actions?: ReactNode
}

function Header({ title, desc, actions }: HeaderProps) {
	return (
		<div className="flex items-center justify-between px-6 py-4">
			<div>
				<Heading level="h2">{title}</Heading>
				{desc && (
					<Text className="text-ui-fg-subtle" size="small">
						{desc}
					</Text>
				)}
			</div>
			{actions}
		</div>
	)
}

function HeaderActionRoot({ className, ...rest }: ComponentProps<'div'>) {
	return (
		<div
			className={cx('flex items-center justify-center gap-x-2', className)}
			{...rest}
		/>
	)
}

function HeaderAction({ className, ...rest }: ComponentProps<typeof Button>) {
	return <Button variant="secondary" size="small" {...rest} />
}

function HeaderActionButton({
	className,
	...rest
}: ComponentProps<typeof Button>) {
	return <Button variant="secondary" size="small" {...rest} />
}

function HeaderActionLink({ className, ...rest }: ComponentProps<typeof Link>) {
	return (
		<Button variant="secondary" size="small" asChild>
			<Link className={cx('text-ui-fg-subtle', className)} {...rest} />
		</Button>
	)
}

function Content({ className, ...rest }: ComponentProps<'div'>) {
	return <div className={cx('space-y-4 divide-y', className)} {...rest} />
}

function ContentGrid({ className, ...rest }: ComponentProps<'div'>) {
	return (
		<div
			className={cx(
				'grid grid-cols-1 gap-x-2 gap-y-4 px-6 py-4 md:grid-cols-3',
				className
			)}
			{...rest}
		/>
	)
}

/**
 * Content Section
 * grid-cols-1 gap-x-2 gap-y-4 px-6 py-4 md:grid-cols-3
 */
function ContentSection({ className, ...rest }: ComponentProps<'div'>) {
	return (
		<div
			className={cx(
				'grid grid-cols-1 gap-x-2 gap-y-5 p-6 md:grid-cols-3',
				className
			)}
			{...rest}
		/>
	)
}

type ContentSectionHeaderProps = Omit<ComponentProps<'div'>, 'title'> & {
	title: string
	desc?: string
	action?: ReactNode
}

function ContentSectionHeader({
	className,
	title,
	desc,
	action,
	...rest
}: ContentSectionHeaderProps) {
	if (action) {
		return (
			<div
				className={cx('col-span-full flex justify-between gap-x-2', className)}
				{...rest}
			>
				<div className="flex flex-col gap-y-1">
					<Heading level="h3">{title}</Heading>
					{desc && (
						<Text className="text-ui-fg-subtle" size="small">
							{desc}
						</Text>
					)}
				</div>
				{action}
			</div>
		)
	}

	return (
		<div className={cx('col-span-full', className)} {...rest}>
			<Heading level="h3">{title}</Heading>
			{desc && (
				<Text className="mt-1 text-ui-fg-subtle" size="small">
					{desc}
				</Text>
			)}
		</div>
	)
}

interface PageSectionRowProps {
	title: string
	value?: ReactNode
	actions?: ReactNode
	className?: string
	classNameTitle?: string
}

function ContentRow({
	title,
	value,
	actions,
	className,
	classNameTitle,
}: PageSectionRowProps) {
	return (
		<div
			className={cx(
				`grid w-full grid-cols-2 items-center gap-4 px-6 py-4 text-ui-fg-subtle`,
				{
					'grid-cols-[1fr_1fr_28px]': !!actions,
				},
				className
			)}
		>
			<Text
				size="small"
				weight="plus"
				leading="compact"
				className={cx(classNameTitle)}
			>
				{title}
			</Text>

			{!ReactIs.isElement(value) ? (
				<Text
					size="small"
					leading="compact"
					className="whitespace-pre-line text-pretty"
				>
					{value ?? '-'}
				</Text>
			) : (
				<div className="flex flex-wrap gap-1">{value}</div>
			)}

			{actions && <div>{actions}</div>}
		</div>
	)
}

export const PageCard = Object.assign(Root, {
	Root,
	Header,
	HeaderActionRoot,
	HeaderAction,
	HeaderActionButton,
	HeaderActionLink,
	Content,
	ContentGrid,
	ContentSection,
	ContentSectionHeader,
	ContentRow,
})
